import { getErrorMessage } from '@/utils/errorUtils';

import { supabase } from '../client';
import { TeeBox, User, getManualPlayerId } from '@/types';
import { getOrCreateTeeBox } from './teeBoxService';
import { findEligibleGameIds } from './gameLinkingUtils';
import { createGame } from '../game/gameCreateService';
import { generateGameName } from '@/utils/gameNameGenerator';
import { notifyBirdieRecorded } from '@/utils/notificationUtils';

/**
 * Create a new score record
 */
export const createScore = async (scoreData: {
  user_id: string;
  course_id: string;
  hole_number: number;
  tee: TeeBox;
  score_type: 'birdie' | 'eagle' | 'double_eagle';
  score_date: string;
  notes?: string;
  gameIds?: string[];
  presentPlayerIds?: string[];
  presentPlayers?: User[];
  manualPlayerId?: string;
}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.error('No authenticated user found');
      return { error: 'Authentication required' };
    }

    // Find the hole to get its ID
    const { data: hole, error: holeError } = await supabase
      .from('holes')
      .select('id')
      .eq('course_id', scoreData.course_id)
      .eq('hole_number', scoreData.hole_number)
      .single();

    if (holeError || !hole) {
      console.error('Error finding hole:', holeError);
      return { error: `Hole not found: ${holeError?.message}` };
    }

    // Get or create tee box
    const { data: teeBoxId, error: teeBoxError } = await getOrCreateTeeBox(hole.id, scoreData.tee);

    if (teeBoxError || !teeBoxId) {
      console.error('Error with tee box:', teeBoxError);
      return { error: `Tee box error: ${teeBoxError}` };
    }

    // Create the score
    // When scoring for a manual player, user_id stays as the real user (for FK),
    // and manual_player_id indicates who the score is attributed to.
    const { data: score, error: scoreError } = await supabase
      .from('scores')
      .insert({
        user_id: scoreData.manualPlayerId ? user.id : scoreData.user_id,
        course_id: scoreData.course_id,
        hole_number: scoreData.hole_number,
        tee: scoreData.tee,
        tee_box_id: teeBoxId,
        score_type: scoreData.score_type,
        score_date: scoreData.score_date,
        notes: scoreData.notes,
        entered_by_user_id: user.id,
        manual_player_id: scoreData.manualPlayerId || null,
      })
      .select()
      .single();

    if (scoreError || !score) {
      console.error('Error creating score:', scoreError);
      return { error: scoreError?.message };
    }

    // Ensure the current user is in the player presence list
    const presentPlayerIds = [...(scoreData.presentPlayerIds || [])];
    if (!presentPlayerIds.includes(user.id)) {
      presentPlayerIds.push(user.id);
    }

    // Record player presence - split real vs manual player IDs
    if (presentPlayerIds.length > 0) {
      const presenceData = presentPlayerIds.map(playerId => {
        const mpId = getManualPlayerId(playerId);
        if (mpId) {
          return { score_id: score.id, profile_id: null, manual_player_id: mpId };
        }
        return { score_id: score.id, profile_id: playerId, manual_player_id: null };
      });

      const { error: presenceError } = await supabase
        .from('score_presence')
        .insert(presenceData);

      if (presenceError) {
        console.error('Error recording player presence:', presenceError);
      }

      // Auto-determine and link eligible games (batch queries)
      let linkedGameIds: string[] = [];
      try {
        const eligibleGameIds = await findEligibleGameIds({
          userId: scoreData.user_id,
          courseId: scoreData.course_id,
          scoreDate: scoreData.score_date,
          presentPlayerIds,
          manualPlayerId: scoreData.manualPlayerId,
        });

        if (eligibleGameIds.length > 0) {
          linkedGameIds = eligibleGameIds;
          const scoreGameLinks = eligibleGameIds.map(gameId => ({
            score_id: score.id,
            game_id: gameId
          }));

          const { error: linkError } = await supabase
            .from('score_games')
            .insert(scoreGameLinks);

          if (linkError) {
            console.error('Error linking score to games:', linkError);
          }
        } else if (scoreData.presentPlayers && scoreData.presentPlayers.length > 0) {
          // No existing game matches — auto-create an open-ended game
          try {
            const { data: newGame, error: gameError } = await createGame({
              name: generateGameName(),
              courseId: scoreData.course_id,
              players: scoreData.presentPlayers,
              durationType: 'open_ended',
            });

            if (!gameError && newGame) {
              linkedGameIds = [newGame.id];
              const { error: linkError } = await supabase
                .from('score_games')
                .insert({ score_id: score.id, game_id: newGame.id });

              if (linkError) {
                console.error('Error linking score to auto-created game:', linkError);
              }
            }
          } catch (autoCreateError) {
            console.error('Error auto-creating game:', autoCreateError);
          }
        }
      } catch (error) {
        console.error('Error auto-linking score to games:', error);
      }

      // Fire-and-forget: notify game members about the score
      if (linkedGameIds.length > 0) {
        notifyBirdieRecorded({
          scoreId: score.id,
          scorerId: scoreData.user_id,
          scoreType: scoreData.score_type,
          holeNumber: scoreData.hole_number,
          courseId: scoreData.course_id,
          gameIds: linkedGameIds,
          isManualPlayer: !!scoreData.manualPlayerId,
          manualPlayerName: scoreData.presentPlayers?.find(
            p => p.manualPlayerId === scoreData.manualPlayerId
          )?.name,
        });
      }
    }

    return { data: score };
  } catch (error) {
    console.error('Unexpected error in createScore:', error);
    return { error: getErrorMessage(error) };
  }
};
